import { heart, heartOutline, arrowUpOutline, arrowDownOutline, checkmarkOutline, lockClosedOutline, radioOutline } from "ionicons/icons";
import moment from "moment";
import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";

import {
  IonChip,
  IonCol,
  IonIcon,
  IonLabel,
  IonLoading,
  IonRow,
} from "@ionic/react";

import { toggleWatchlistItem } from "../../redux/slices/auctionItemsSlice";
import { WATCH_LIST, getEndPoint } from "../../util/api/enpoints";
import Text from "../CommonStyles/Text";
import { AuctionItem, UserBidStatus } from "./Index";
import {
  ErrorMessage,
  LastBidContainer,
  ListIconContainer,
  ListItemRow,
} from "./styledComponents";
import { saveScrollAndNavigate } from "./helpers";
import {DateTime} from "luxon";
import '../../theme/revamp.css';

interface Params {
  eventcode: string
}

export const ListViewItems = ({ state, items }) => {
  const local = DateTime.local()
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { eventcode } = useParams()
  const accountStatus = useSelector((state: any) => state.user.accountstatus);

  const getLocalDateString = (dateString) => {
    const centralDate = DateTime.fromISO(dateString,{zone: 'America/Chicago'})
    const localDate = centralDate.setZone(local.zoneName)
    const localDateString = localDate.toString()
    return localDateString
  }

  const closingTime = (closing: string) => {
    const then: any = moment(closing);
    const now: any = moment();
    const diff = moment.duration(then.diff(now));
    var days = Math.floor(diff.asDays());
    var hours = diff.hours();
    var min = diff.minutes()
    if (days < 1){
      return `${hours}h ${min}m`
    }
    else if (days < 1 && hours < 1 && min < 1) {
      return `1m`
    }
    return `${days}d ${hours}h`;
  };

  const itemStatus = (item: any) => {
    return (item.bidstatus === "biddingclosed" || (item.quantityavailable === 0 && item.unlimitedquantity === false))
        ? "Closed"
        : closingTime(getLocalDateString(item.closingcst));
  };

  const onWatchListClick = (itemid: number) => (e: any) => {
    e.stopPropagation();
    dispatch(toggleWatchlistItem({ url: getEndPoint(WATCH_LIST), itemid }));
  };

  const onItemClick = (itemId: number) => {
    saveScrollAndNavigate(itemId, eventcode, navigate)
  };

  const handleBuyItNowList = (item: any, status: UserBidStatus) => {
    if (item.quantityavailable > 0 || item.unlimitedquantity) {
      if (item.buyitnowavailable && item.buyitnowonly === false) {
        return (
            <div>
              <IonRow>
                <IonCol style={{ display: "flex" }}>
                  <LastBidContainer status={status} className="lastBidContainer">
                    <Text fontSize="14px" fontWeight={600}>
                      ${item.lastbid}
                    </Text>
                  </LastBidContainer>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol>
                  <Text fontSize="12px" color="#a2a2a2">
                    ${item.buyitnowprice}
                  </Text>
                  <Text fontSize="12px" color="#a2a2a2">
                    Buy Now
                  </Text>
                </IonCol>
              </IonRow>
            </div>
        );
      } else if (item.buyitnowonly) {
        return (
            <IonRow>
              <IonCol>
                <Text fontSize="12px" color="#a2a2a2">
                  ${item.buyitnowprice}
                </Text>
              </IonCol>
              <IonCol>
                <Text fontSize="12px" color="#a2a2a2">
                  $$$
                </Text>
              </IonCol>
            </IonRow>
        );
      } else {
        if (item.itemisliveonly) {
          return (
              <IonRow>
                <IonCol>
                  <Text fontSize="14px">Live Auction</Text>
                </IonCol>
              </IonRow>
          )
        }
        else {
          return (
              <IonRow>
                <IonCol>
                  <Text fontSize="14px">${item.lastbid}</Text>
                </IonCol>
              </IonRow>
          )
        }
      }
    }
    else {
      if (item.buyitnowavailable) {
        return (
            <div>
              <IonRow>
                <IonCol style={{ display: "flex" }}>
                  <LastBidContainer status={status} className="lastBidContainer">
                    <Text fontSize="14px" fontWeight={600}>
                      SOLD OUT
                    </Text>
                  </LastBidContainer>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol>
                  <Text fontSize="12px" color="#a2a2a2">
                    ${item.buyitnowprice}
                  </Text>
                  <Text fontSize="12px" color="#a2a2a2">
                    Buy Now
                  </Text>
                </IonCol>
              </IonRow>
            </div>
        );
      } else {
        return (
            <IonRow>
              <IonCol>
                <Text fontSize="14px">SOLD OUT</Text>
              </IonCol>
            </IonRow>
        );
      }
    }
  };

  const renderIconOnList = (
    status: UserBidStatus = "default",
    item: AuctionItem,
    isWon: boolean,
    isClosedOther: boolean,
    isLive: boolean
  ) => {
    if (isWon) {
      return (
        <IonIcon
          icon={checkmarkOutline}
          slot="icon-only"
          style={{ width: "1.4rem", height: "1.4rem", color: "var(--as-won)" }}
        />
      );
    }

    if (isClosedOther) {
      return (
        <IonIcon
          icon={lockClosedOutline}
          slot="icon-only"
          style={{ width: "1.4rem", height: "1.4rem", color: "#555" }}
        />
      );
    }

    if (status === "highBidder") {
      return (
        <IonIcon
          icon={arrowUpOutline}
          slot="icon-only"
          style={{ width: "1.5rem", height: "1.5rem", color: "var(--as-win)" }}
        />
      );
    }

    if (status === "losingBidder") {
      return (
        <IonIcon
          icon={arrowDownOutline}
          slot="icon-only"
          style={{ width: "1.5rem", height: "1.5rem", color: "var(--as-out)" }}
        />
      );
    }

    if (isLive) {
      return (
        <IonIcon
          icon={radioOutline}
          slot="icon-only"
          style={{ width: "1.4rem", height: "1.4rem", color: "#555" }}
        />
      );
    }

    if (accountStatus !== 'guest') {
      return (
          <button
              className="as-heart"
              style={{ position: "static", width: "1.7rem", height: "1.7rem" }}
              onClick={onWatchListClick(item?.itemid)}
          >
            <IonIcon
                icon={item.watchlist ? heart : heartOutline}
                style={{ width: "1.4rem", height: "1.4rem", color: item.watchlist ? '#d2222d' : '#888' }}
            />
          </button>
      );
    }

  };

  if (state === "loading") {
    return (
      <IonRow>
        <IonLoading isOpen></IonLoading>
      </IonRow>
    );
  }

  if (state === "failed") {
    return (
      <ErrorMessage>
        Sorry, we are unable to fetch auction items at the moment.
      </ErrorMessage>
    );
  }

  return items.map((item: AuctionItem, i) => {
    const getStatus = (): UserBidStatus => {
      const stillOpen =  
        moment().diff(moment(getLocalDateString(item.closingcst))) < 0
      if (item.currentlywinningbid && item.accountbiddingon && stillOpen) {
        return "highBidder"
      }
      if (item.currentlywinningbid && item.accountbiddingon && !stillOpen) {
        return "winner"
      }
      if (!item.currentlywinningbid && item.accountbiddingon) {
        return "losingBidder"      
      }
      return "default"
    }
    const status = getStatus()
    const isClosed = item.bidstatus === "biddingclosed"
    const isSoldOut = item.quantityavailable === 0 && item.unlimitedquantity === false
    const isWon = (isClosed || isSoldOut) && item.accountbiddingon && item.currentlywinningbid
    const isClosedOther = (isClosed || isSoldOut) && !isWon
    const isLive = item.itemisliveonly && !isClosed
    const time = (isWon || isClosedOther) ? 'Ended' : itemStatus(item)
    return (
      <ListItemRow
        status={status}
        guest={accountStatus === 'guest'}
        won={isWon}
        closedOther={isClosedOther}
        key={item.itemid}
        onClick={() => onItemClick(item.itemid)}
      >
        {accountStatus !== 'guest' &&
            <>
              <ListIconContainer status={status} guest={accountStatus === 'guest'} won={isWon} style={{ paddingLeft: "0.25rem" }}>
                {renderIconOnList(status, item, isWon, isClosedOther, isLive)}
              </ListIconContainer>
            </>
        }

        <div>
          <Text fontSize="12px" style={{ paddingLeft: "0.50rem" }}>{item.name}</Text>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            justifyItems: "center",
          }}
        >
          {item.tags &&
            item.tags.split(",").map((tag, i) => (
              <IonChip color="black" style={{ padding: 5, height: 24 }}>
                <IonLabel color="black" style={{ fontSize: 12 }}>
                  {tag}
                </IonLabel>
              </IonChip>
            ))}
        </div>
        <div>{handleBuyItNowList(item, status)}</div>
        <div style={{ paddingRight: "0..25rem" }}>
          <Text fontSize="12px">{time}</Text>
        </div>
      </ListItemRow>
    );
  });
};
